import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Typography, Box, Button, makeStyles, Container,
} from '@material-ui/core';
import CallIcon from '@material-ui/icons/Call';
import EmailIcon from '@material-ui/icons/Email';
import { useHistory } from 'react-router-dom';
import { IoLogoFacebook } from 'react-icons/io';
import { FcGoogle } from 'react-icons/fc';
import { FaTwitter, FaGithub } from 'react-icons/fa';
import MyForm from '../../form/MyForm';
import pageUtilsPropTypes from '../../page/page-prop-types';
import { AppState } from '../../state/MyStateProvider';
import formFieldsPropTypes from '../../form/form-prop-types';
import prepareFormikInitialValues from '../../form/form-helpers';
import seedLogo from '../../images/logo.svg';
import { seedAppIcons } from '../../icon/seed-icons';
import { useSearchParams } from '../../route/route-helpers';
import AuthPage from '../../page/AuthPage';
import FirebaseRepository from '../FirebaseRepository';
import { FirebaseState } from '../MyFirebaseProvider';
import { firebaseLoginFields, firebaseLoginValidationSchema } from './firebase-forms';

const useStyles = makeStyles((theme) => ({
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  logo: {
    width: 200,
    height: 'auto',
  },
  twitter: {
    color: '#1DA1F2',
  },
  github: {
    color: '#181717',
  },
  facebook: {
    color: '#1877F2',
  },
  disabledIcon: {
    color: theme.palette.text.disabled,
  },
}));

const SeedFirebaseLoginPage = (props) => {
  const {
    fields,
    validationSchema,
    pageUtils,
    logo,
    applicationName,
    icons,
    providers,
  } = props;
  const classes = useStyles();
  const history = useHistory();
  const intl = useIntl();
  const search = useSearchParams();
  const redirect = search.get('redirect');
  const deleteUser = search.get('delete');
  const { state, dispatch } = React.useContext(AppState);
  const { firebaseConfig } = React.useContext(FirebaseState);

  const [isLoading, setIsLoading] = React.useState(false);
  const [firebaseRepo] = React.useState(new FirebaseRepository(state, dispatch, firebaseConfig));

  React.useEffect(() => {
    if (deleteUser) {
      const diff = Math.abs(new Date() - new Date(parseInt(deleteUser, 10)));
      const minutes = Math.floor((diff / 1000) / 60);
      // if within 1 minute, show snackbar.
      if (minutes < 1) {
        pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.deleteUser.success' }), 'success');
      }
    }

    return () => {

    };
  }, [pageUtils, deleteUser, intl]);

  const handleSubmitLogin = (values) => {
    setIsLoading(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.loginEmailPassword(values),
      (payload) => {
        firebaseRepo.saveUser(payload.user);
        if (redirect) {
          history.push(redirect);
        }
      },
      (error) => {
        if (error.message) {
          pageUtils.showSnackbar(error.message, 'error');
        } else {
          pageUtils.showSnackbar(error, 'error');
        }
        setIsLoading(false);
      },
      () => { },
    );
  };

  const handleClickSignInProvider = (provider) => {
    setIsLoading(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.signInProvider(provider),
      (payload) => {
        firebaseRepo.saveUser(payload.user);
        if (redirect) {
          history.push(redirect);
        }
      },
      (error) => {
        if (error.message) {
          pageUtils.showSnackbar(error.message, 'error');
        } else {
          pageUtils.showSnackbar(error, 'error');
        }
        setIsLoading(false);
      },
      () => { },
    );
  };

  const initialValues = prepareFormikInitialValues(null, true, fields);

  const iconProviders = {
    'google.com': FcGoogle,
    'facebook.com': IoLogoFacebook,
    'twitter.com': FaTwitter,
    'github.com': FaGithub,
    phone: CallIcon,
    emailLink: EmailIcon,
  };
  const classNameProviders = {
    'google.com': null,
    'facebook.com': classes.facebook,
    'twitter.com': classes.twitter,
    'github.com': classes.github,
    phone: null,
    emailLink: null,
  };
  const hrefProviders = {
    'google.com': null,
    'facebook.com': null,
    'twitter.com': null,
    'github.com': null,
    phone: 'login-phone',
    emailLink: 'login-link',
  };

  return (
    <AuthPage isLoading={isLoading} logo={logo}>
      <div className={classes.logoContainer}>
        <img className={classes.logo} alt={`${applicationName} Logo`} src={logo} />
      </div>
      <Typography align="center" component="h1" variant="h5">
        <FormattedMessage id="seed.firebase.authentication.login.heading" values={{ applicationName }} />
      </Typography>
      <Box mt={6} />
      <MyForm
        fields={fields}
        validationSchema={validationSchema}
        onSubmit={handleSubmitLogin}
        disabled={isLoading}
        submitButtonLabelId="seed.login.button.submit"
        initialValues={initialValues}
        icons={icons}
      />
      <Box mt={3} align="center">
        <Button href="/register">
          <FormattedMessage id="seed.button.register" />
        </Button>
        <Button href="/reset-password">
          <FormattedMessage id="seed.firebase.authentication.resetPassword.button" />
        </Button>
        <Container maxWidth="xs">
          {
            providers.map((prov) => {
              const Icon = iconProviders[prov];
              const className = classNameProviders[prov];
              const href = hrefProviders[prov];
              return (
                <Box mt={1} key={`button-sign-in-provider-${prov}`}>
                  <Button
                    fullWidth
                    disabled={isLoading}
                    variant="outlined"
                    href={href}
                    onClick={!href ? () => handleClickSignInProvider(prov) : null}
                    startIcon={(
                      <Icon
                        className={clsx({
                          [className]: !isLoading,
                          [classes.disabledIcon]: isLoading,
                        })}
                      />
                  )}
                  >
                    <FormattedMessage
                      id="seed.firebase.authentication.provider.signin.button"
                      values={{
                        provider: intl.formatMessage({ id: prov }),
                      }}
                    />
                  </Button>
                </Box>
              );
            })
          }
        </Container>
      </Box>
    </AuthPage>
  );
};

SeedFirebaseLoginPage.propTypes = {
  fields: PropTypes.arrayOf(formFieldsPropTypes),
  validationSchema: PropTypes.object,
  pageUtils: pageUtilsPropTypes.isRequired,
  logo: PropTypes.string,
  applicationName: PropTypes.string,
  icons: PropTypes.objectOf(PropTypes.object),
  providers: PropTypes.arrayOf(PropTypes.string),
};

SeedFirebaseLoginPage.defaultProps = {
  fields: firebaseLoginFields,
  validationSchema: firebaseLoginValidationSchema,
  logo: seedLogo,
  applicationName: 'react-seed',
  icons: seedAppIcons,
  providers: [
    'google.com',
    'facebook.com',
    'twitter.com',
    'github.com',
    'phone',
    'emailLink',
  ],
};

export default SeedFirebaseLoginPage;
